import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faLocationDot,
} from '@fortawesome/pro-regular-svg-icons';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { User, Job } from '@core/generated/gql-types';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import { ratingActions } from '@core/rating/rating.action';
import { getWorkerCvSummary } from '@/modules/workers/utils/cvProfile.utils';
import { isFeatureEnabled } from '@/utils/featureFlags';
import { useCoreLoopConfigurationStore } from '@core/coreLoopConfiguration/useCoreLoopConfigurationStore';
import { formatReliabilityScore } from '@core/reliabilityScore/reliabilityScore.util';

interface WorkerApplicationProps {
  worker: User;
  job: Job;
  coverLetter?: string | null;
  workerRateInCents?: number | null;
  showWorkerPayRate?: boolean;
  onAssignWorker: (worker: User) => void;
}

const formatHourlyRate = (cents?: number | null): string | null => {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) return null;
  const dollars = cents / 100;
  return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
};

const WorkerApplication = ({
  worker,
  job,
  coverLetter,
  workerRateInCents,
  showWorkerPayRate = true,
  onAssignWorker,
}: WorkerApplicationProps) => {
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const [workerRating, setWorkerRating] = useState<number | null>(null);
  const jobInPast = job?.startDate
    ? dayjs(parseInt(job.startDate, 10)).isBefore(dayjs())
    : true;
  const { getTerm } = useTemplateStore();
  const summary = getWorkerCvSummary(worker);
  const hasCandidateSummary = Boolean(summary);
  const showCandidateSummary =
    hasCandidateSummary && isFeatureEnabled('candidateSummary');
  const displayCandidateSuburbForClients = useCoreLoopConfigurationStore(
    (state) =>
      state.coreLoopConfigurationItems?.displayCandidateSuburbForClients ===
      true,
  );
  const displayReliabilityScore = useCoreLoopConfigurationStore(
    (state) =>
      state.coreLoopConfigurationItems?.displayReliabilityScore === true,
  );
  const applicantLocation = [worker?.address?.suburb, worker?.address?.state]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');
  const reliability = formatReliabilityScore(worker?.reliabilityScore);
  const hourlyRate = showWorkerPayRate
    ? formatHourlyRate(workerRateInCents)
    : null;

  useEffect(() => {
    if (!worker?.id) return;

    let isCancelled = false;

    const loadRating = async () => {
      try {
        const rating = await ratingActions.fetchRatingForUserId(worker.id);
        if (isCancelled) return;
        setWorkerRating(
          typeof rating === 'number' && Number.isFinite(rating) ? rating : null,
        );
      } catch (error) {
        console.error('Error fetching worker rating:', error);
        if (!isCancelled) setWorkerRating(null);
      }
    };

    void loadRating();

    return () => {
      isCancelled = true;
    };
  }, [worker?.id]);

  return (
    <div className="flex w-full flex-col rounded-md bg-white">
      <div className="px-3 pt-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex shrink-0 flex-col items-start">
              <div className="flex h-10 w-10 items-center overflow-hidden rounded-full bg-gray-100">
                {worker?.profilePictureUrl ? (
                  <img
                    src={worker.profilePictureUrl}
                    alt={`${worker?.firstName ?? ''} ${worker?.lastName ?? ''}`.trim()}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-gray-200" />
                )}
              </div>
              {workerRating != null ? (
                <p className="mt-0.5 flex items-center gap-1 text-sm text-gray-900">
                  <FontAwesomeIcon
                    icon={faStar}
                    className="h-3.5 w-3.5 text-amber-400"
                  />
                  <span>{workerRating.toFixed(1)}</span>
                </p>
              ) : null}
            </div>
            <div className="flex min-w-0 flex-col">
              <p className="truncate text-sm font-semibold text-gray-900">{`${worker?.firstName ?? ''} ${worker?.lastName ?? ''}`.trim()}</p>
              {displayCandidateSuburbForClients && applicantLocation ? (
                <p className="flex items-center gap-1.5 text-sm text-gray-400">
                  <FontAwesomeIcon
                    icon={faLocationDot}
                    title="Applicant location"
                    className="h-3 w-3"
                  />
                  {applicantLocation}
                </p>
              ) : null}
              <p className="text-sm text-gray-400">
                {job?.classification?.name || 'Job classification not set'}
              </p>
              {hourlyRate ? (
                <p className="text-sm text-gray-900">{hourlyRate}</p>
              ) : null}
            </div>
          </div>
          <button
            className={`shrink-0 cursor-pointer rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 ${jobInPast ? 'cursor-not-allowed opacity-50' : ''}`}
            type="button"
            title={
              !worker
                ? `No ${getTerm(TemplateTerm.WORKER, false)} selected`
                : `Assign ${getTerm(TemplateTerm.WORKER, false)}`
            }
            onClick={() => {
              onAssignWorker(worker);
            }}
          >
            Assign
          </button>
        </div>
      </div>
      {coverLetter?.trim() ? (
        <blockquote className="mx-3 mt-3 text-sm break-words text-gray-700 italic">
          “{coverLetter.trim()}”
        </blockquote>
      ) : null}

      {showCandidateSummary && isSummaryExpanded ? (
        <div className="mt-3 space-y-2 border-t border-gray-200 px-3 py-3">
          {displayReliabilityScore && reliability ? (
            <p className="text-left text-sm text-gray-600">
              Reliability: {reliability}
            </p>
          ) : null}
          <p className="text-left text-sm leading-5 whitespace-pre-wrap text-gray-700">
            {summary}
          </p>
        </div>
      ) : null}

      {showCandidateSummary ? (
        <button
          type="button"
          aria-expanded={isSummaryExpanded}
          onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
          className={`${isSummaryExpanded ? '' : 'mt-3'} flex w-full cursor-pointer items-center justify-center gap-2 border-t border-gray-200 py-2.5 text-sm font-medium text-blue-600 hover:text-blue-700`}
        >
          Candidate Summary
          <FontAwesomeIcon
            icon={isSummaryExpanded ? faChevronUp : faChevronDown}
            className="h-3 w-3"
          />
        </button>
      ) : null}
    </div>
  );
};

export default WorkerApplication;
